/**
 * Ricerca fuzzy condivisa per liste già caricate in memoria (dropdown, tabelle locali).
 *
 * Stesso pattern di gustovo-platform (`packages/shared/src/search.ts`): Fuse.js così
 * refusi, lettere mancanti e sostituzioni trovano comunque, con risultati ordinati per
 * rilevanza (prima gli esatti, poi i typo) - comportamento Spotlight/Algolia.
 *
 * Taratura (identica a Gustovo, validata sui suoi test con typo):
 * - insensibile agli accenti ("citta" trova "Città") via normalizzazione NFD;
 * - ignoreLocation: conta ovunque nel testo, non solo all'inizio;
 * - threshold 0.4: tollera "scarlati" -> "Scarlatti" senza inondare di risultati.
 *
 * NOTA: per dataset grandi (immobili/clienti di agenzia con 500+ record) la ricerca
 * resta server-side (`hooks/useSearchDropdown`, pg_trgm lato DB): Fuse qui serve per
 * le liste locali e come rerank, non può trovare righe mai scaricate.
 *
 * Tenere l'array `keys` stabile (module scope, non inline) così il useMemo dei
 * chiamanti può davvero cachare l'indice.
 */
import Fuse, { type IFuseOptions, type FuseOptionKey } from 'fuse.js'

// Combining diacritical marks (U+0300..U+036F) - rimossi dopo NFD per il folding accenti.
const COMBINING_DIACRITICS = /[̀-ͯ]/g

export function normalizeForSearch(input: string): string {
  return (input ?? '')
    .normalize('NFD')
    .replace(COMBINING_DIACRITICS, '')
    .toLowerCase()
    .trim()
}

// Risolve un path Fuse (anche dotted/array) contro un oggetto.
function resolvePath(obj: unknown, path: string | string[]): unknown {
  const keys = Array.isArray(path) ? path : path.split('.')
  let value: unknown = obj
  for (const k of keys) {
    if (value == null) return undefined
    value = (value as Record<string, unknown>)[k]
  }
  return value
}

// Opzioni base condivise da ogni ricerca fuzzy dell'app.
const BASE_OPTIONS: IFuseOptions<unknown> = {
  includeScore: true,
  ignoreLocation: true,
  threshold: 0.4,
  minMatchCharLength: 1,
  // Normalizza (accenti/case) ogni valore indicizzato così indice e query concordano.
  getFn: (obj, path) => {
    const value = resolvePath(obj, path)
    if (Array.isArray(value)) return value.map((v) => normalizeForSearch(String(v)))
    return value == null ? '' : normalizeForSearch(String(value))
  },
}

/** Costruisce un indice Fuse riusabile. Memoizzarlo sulla lista quando possibile. */
export function createSearcher<T>(items: T[], keys: FuseOptionKey<T>[]): Fuse<T> {
  return new Fuse(items, { ...(BASE_OPTIONS as IFuseOptions<T>), keys })
}

/**
 * Filtra + ranka una lista su query libera. Query vuota → lista invariata
 * (il chiamante mantiene il proprio ordinamento quando non si digita).
 */
export function searchList<T>(items: T[], query: string, keys: FuseOptionKey<T>[]): T[] {
  const q = normalizeForSearch(query)
  if (!q) return items
  return createSearcher(items, keys).search(q).map((r) => r.item)
}

/**
 * Check fuzzy booleano di una query contro pochi campi stringa. Query vuota → true.
 * Per liste raggruppate/annidate dove `searchList` non calza. Costruisce un indice da
 * un record: usarlo su collezioni piccole.
 */
export function matchesQuery(query: string, ...fields: Array<string | null | undefined>): boolean {
  const q = normalizeForSearch(query)
  if (!q) return true
  const record = { __f: fields.map((f) => f ?? '') }
  const fuse = new Fuse([record], { ...(BASE_OPTIONS as IFuseOptions<typeof record>), keys: ['__f'] })
  return fuse.search(q).length > 0
}
